import React, { useState } from 'react';
import { CheckIcon, ShieldCheckIcon, ChevronDownIcon, ArrowRightIcon, TargetIcon, MessageSquareIcon, CalendarIcon, ZapIcon } from 'lucide-react';
interface BonusSectionProps {
  totalPrice: number;
  originalPrice: number;
  cartUrl: string;
  onPurchaseClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}
export function BonusSection({
  totalPrice,
  originalPrice,
  cartUrl,
  onPurchaseClick
}: BonusSectionProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const handleCTAClick = () => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'The $10K AI Content Engine Bonuses',
        content_type: 'product',
        value: 47,
        currency: 'USD'
      });
    }
  };
  const faqs = [{
    q: "I'm not sure I'm ready for a $10K retainer yet.",
    a: "That's exactly why the Offer Clarity Kit exists. It meets you where you are and shows you the path — even if you start at $4K and build from there."
  }, {
    q: "I'm not technical.",
    a: "None of these resources require tech skills. They're strategy + language tools. Copy, paste, adapt, send."
  }, {
    q: "I don't have an audience or credentials.",
    a: 'The scripts are built specifically for this. The Permissionless Audit and the DM openers work without a big following or a portfolio.'
  }, {
    q: "What if I'm too busy to use them?",
    a: 'The planner was designed for exactly this. 4 hours per week. Even bad weeks. Even exhausted weeks.'
  }, {
    q: 'Is this just theory or does it actually work?',
    a: 'One student went from zero to $7,500/month in 19 days. The 30-day sprint plan is built from real results, not hypotheticals.'
  }, {
    q: 'What exactly do I get after I pay?',
    a: 'Instant download. You get the full PDF guide plus all 3 bonus resources immediately. No waiting, no drip content, no hoops.'
  }, {
    q: 'What if AI tools change or platforms update?',
    a: "The architecture principle doesn't change. Tools evolve — systems adapt. You learn to build the machine, not depend on one tool."
  }, {
    q: "Why is it only $27? What's the catch?",
    a: 'No catch. The low price is intentional — fast adoption means more success stories. Your results become the best marketing we could ask for.'
  }];
  return <section className="pt-2 pb-14 relative overflow-hidden bg-dark-950" data-id="element-21">
      {/* Background Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" data-id="element-22"></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23">
        {/* 1. HEADLINE & SUB-HEADLINE */}
        <div className="text-center mb-12" data-id="element-24">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-25">
            Most People Fail Not Because They Lack Skills — But Because Nobody
            Gave Them the Offer, the Scripts, or the Plan.{' '}
            <span className="text-cyan-400 text-glow" data-id="element-26">
              Here Are All Three. For $27.
            </span>
          </h2>
          <p className="text-xl text-gray-400 font-light" data-id="element-27">
            For the corporate professional who already knows they want out — but
            keeps losing weeks to "what do I sell?, how do I say it?, when do I
            actually do this?"
          </p>
        </div>

        {/* 3. OPENING HOOK */}
        <div className="prose prose-invert prose-cyan max-w-none text-lg text-gray-300 space-y-6 mb-12" data-id="element-28">
          <p data-id="element-29">There's a specific moment when everything breaks down.</p>
          <p data-id="element-30">
            It's not when you decide you want to build something of your own.
          </p>
          <p data-id="element-31">It's three weeks later.</p>
          <p data-id="element-32">
            You have the course. You have the intention. You have the energy —
            or what's left of it after a full day at work.
          </p>
          <p data-id="element-33">And you sit down in front of the screen.</p>
          <p data-id="element-34">And nothing moves.</p>
          <p className="italic text-gray-400 border-l-2 border-cyan-500/50 pl-4 py-1" data-id="element-35">
            What exactly am I selling? How much do I charge? What do I say when
            a lead actually responds? How am I supposed to do this when I get
            home at 7pm completely wrecked?
          </p>
          <p data-id="element-36">
            That moment — that silence in front of the screen — kills more
            businesses than lack of knowledge ever will.
          </p>
          <p data-id="element-37">Not because the person isn't capable.</p>
          <p data-id="element-38">
            But because nobody gave them the right tools at the right moment.
          </p>
        </div>

        {/* 4. AGITATION */}
        <div className="prose prose-invert prose-cyan max-w-none text-lg text-gray-300 space-y-6 mb-16" data-id="element-39">
          <p data-id="element-40">
            Most courses teach you the <em data-id="element-41">what</em>.
          </p>
          <p data-id="element-42">
            They show you the system. They walk you through the framework. They
            give you the vision.
          </p>
          <p data-id="element-43">And then they leave you alone with:</p>
          <ul className="space-y-2 list-none pl-0" data-id="element-44">
            <li className="flex items-start gap-3" data-id="element-45">
              <span className="text-cyan-500 mt-1" data-id="element-46">•</span>
              <span data-id="element-47">A notebook full of highlighted ideas</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-48">
              <span className="text-cyan-500 mt-1" data-id="element-49">•</span>
              <span data-id="element-50">An offer you can't finish defining</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-51">
              <span className="text-cyan-500 mt-1" data-id="element-52">•</span>
              <span data-id="element-53">Sales scripts you're supposed to "write yourself"</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-54">
              <span className="text-cyan-500 mt-1" data-id="element-55">•</span>
              <span data-id="element-56">A blank calendar that produces anxiety</span>
            </li>
          </ul>
          <p data-id="element-57">
            And the same question on repeat:{' '}
            <strong data-id="element-58">where do I actually start TODAY?</strong>
          </p>
          <p data-id="element-59">This isn't a motivation problem.</p>
          <p data-id="element-60">This is an execution infrastructure problem.</p>
          <p data-id="element-61">
            The burned-out corporate professional doesn't need more inspiration.
          </p>
          <p data-id="element-62">
            They need to know exactly what to say, what to send, and what to do
            — even on the days when they have nothing left after work.
          </p>
          <p className="text-xl font-bold text-white" data-id="element-63">
            That's what these 3 resources fix.
          </p>
        </div>

        {/* 5. BRIDGE */}
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-8 mb-16 text-center" data-id="element-64">
          <p className="text-xl text-gray-300 mb-4" data-id="element-65">
            I built these 3 resources for one reason:
          </p>
          <p className="text-2xl font-bold text-white mb-6" data-id="element-66">
            Knowledge without execution tools is just expensive journaling.
          </p>
          <p className="text-lg text-gray-400" data-id="element-67">
            These aren't "bonus filler material."
            <br data-id="element-68" />
            They're the 3 pieces that turn what you know into real action, this
            week.
            <br data-id="element-69" />
            <strong className="text-cyan-400" data-id="element-70">
              And today, they come free with your investment.
            </strong>
          </p>
        </div>

        {/* 6. BONUS #1 CARD */}
        <div className="bg-dark-900 border border-cyan-500/20 rounded-xl p-8 mb-12 shadow-[0_0_30px_rgba(0,240,255,0.05)] relative overflow-hidden" data-id="element-71">
          <div className="absolute top-0 right-0 bg-cyan-500 text-black font-bold px-4 py-1 rounded-bl-lg text-sm" data-id="element-72">
            FREE - Value $197
          </div>

          <div className="flex items-center gap-3 mb-4" data-id="element-73">
            <TargetIcon className="w-8 h-8 text-cyan-400" data-id="element-74" />
            <h3 className="text-3xl font-bold text-white" data-id="element-75">BONUS #1</h3>
          </div>
          <h4 className="text-2xl font-bold text-cyan-400 mb-2" data-id="element-76">
            The 30-Minute Offer Clarity Kit
          </h4>
          <p className="text-xl text-gray-300 mb-8" data-id="element-77">
            "Build a $10K/mo Retainer in One Sitting"
          </p>

          <div className="relative rounded-xl overflow-hidden border border-cyan-500/30 bg-dark-950 p-2 shadow-2xl mb-8 box-glow" data-id="element-78">
            <img src="https://cdn.magicpatterns.com/uploads/nwEQ5iTXBijvVPPqeC25XN/bono_1_the_10k_AI.png" alt="The 30-Minute Offer Clarity Kit" className="w-full h-auto rounded-lg" data-id="element-79" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-900/60 via-transparent to-transparent pointer-events-none" data-id="element-80"></div>
          </div>

          <div className="space-y-6 text-gray-300" data-id="element-81">
            <div data-id="element-82">
              <strong className="text-white block mb-2" data-id="element-83">
                The problem it solves:
              </strong>
              <p data-id="element-84">
                The question that stops almost everyone who starts: "What
                exactly am I selling?" If you can't explain your offer in one
                sentence, you can't sell it. And if you can't sell it, you don't
                have a business.
              </p>
              <p className="mt-2" data-id="element-85">
                The 30-Minute Offer Clarity Kit is a 16-slide working guide that
                takes you from "I don't know what to sell" to "I have my $10K
                retainer defined and ready to send" — in a single 30-minute
                session.
              </p>
            </div>

            <div data-id="element-86">
              <strong className="text-white block mb-4" data-id="element-87">What's inside:</strong>
              <ul className="space-y-3" data-id="element-88">
                {['The Retainer Rule that makes $10K feel logical — not ambitious', 'The Pick Your Lane Test — choose between 3 lanes in 2 minutes (zero indecision)', "A Deliverable Menu — select, don't invent (3 ready-made packages at $10K)", 'The 3-Tier Pricing Ladder — Starter $4K / Core $10K / Premium $15K (with anchors that make Core the obvious choice)', 'A One-Screen Offer Page ready to paste into DMs or email today', '5 Fit-Check Questions to qualify $10K leads fast (before you waste a call)', 'A Boundaries & Exclusions Block (copy/paste) that protects your margin and your time', '3 Complete $10K Retainer Examples — illustrated, ready to adapt', 'The "Send It Today" Checklist — review this before sending any offer'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-89">
                    <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-90" />
                    <span data-id="element-91">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-dark-950 p-4 rounded-lg border border-cyan-900/30" data-id="element-92">
              <strong className="text-cyan-400" data-id="element-93">The outcome:</strong> You leave
              this kit with a defined offer, a price, clear boundaries, and a
              template ready to send. No improvising. No "figure it out
              yourself."
            </div>
          </div>
        </div>

        {/* 7. BONUS #2 CARD */}
        <div className="bg-dark-900 border border-cyan-500/20 rounded-xl p-8 mb-12 shadow-[0_0_30px_rgba(0,240,255,0.05)] relative overflow-hidden" data-id="element-94">
          <div className="absolute top-0 right-0 bg-cyan-500 text-black font-bold px-4 py-1 rounded-bl-lg text-sm" data-id="element-95">
            FREE - Value $297
          </div>

          <div className="flex items-center gap-3 mb-4" data-id="element-96">
            <MessageSquareIcon className="w-8 h-8 text-cyan-400" data-id="element-97" />
            <h3 className="text-3xl font-bold text-white" data-id="element-98">BONUS #2</h3>
          </div>
          <h4 className="text-2xl font-bold text-cyan-400 mb-2" data-id="element-99">
            Corporate-to-Creator Positioning Scripts
          </h4>
          <p className="text-xl text-gray-300 mb-8" data-id="element-100">
            "DMs, Calls & Objection Killers (Built to Close $10K Retainers)"
          </p>

          <div className="relative rounded-xl overflow-hidden border border-cyan-500/30 bg-dark-950 p-2 shadow-2xl mb-8 box-glow" data-id="element-101">
            <img src="https://cdn.magicpatterns.com/uploads/w2ff4Y8o1wdT63R1TfZ4d9/BONO_2_THE_10K_AI.png" alt="Corporate-to-Creator Positioning Scripts" className="w-full h-auto rounded-lg" data-id="element-102" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-900/60 via-transparent to-transparent pointer-events-none" data-id="element-103"></div>
          </div>

          <div className="space-y-6 text-gray-300" data-id="element-104">
            <div data-id="element-105">
              <strong className="text-white block mb-2" data-id="element-106">
                The problem it solves:
              </strong>
              <p data-id="element-107">
                You have the offer. You open the chat. And you freeze. What do I
                say? How do I start without sounding like a pitch? What happens
                when they say "I need to think about it"?
              </p>
              <p className="mt-2" data-id="element-108">
                Fear of selling isn't really fear of rejection. It's fear of not
                knowing exactly what to say. This 15-slide swipe file gives you
                the exact language to sound like a premium operator — not a
                desperate freelancer.
              </p>
            </div>

            <div data-id="element-109">
              <strong className="text-white block mb-4" data-id="element-110">What's inside:</strong>
              <ul className="space-y-3" data-id="element-111">
                {['The Positioning Shift — from "I make content" to "I install infrastructure" (the reframe that justifies $10K)', 'Your Identity Line — template + 3 ready examples to use today', '3 DM Openers that start real conversations (no pitchy vibe, no cringe)', 'The "Permissionless Audit" Script — 6 lines that create instant authority without giving away your strategy', 'Mini CTA Library — 4 soft, high-status closing options to choose based on context', 'The 15-Minute Call Flow (minute-by-minute) to close a $10K retainer in one short call', "3 Closing Variations — direct / assumptive / consultative (match to the lead's energy)"].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-112">
                    <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-113" />
                    <span data-id="element-114">{item}</span>
                  </li>)}
                <li className="flex items-start gap-3" data-id="element-115">
                  <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-116" />
                  <div data-id="element-117">
                    <span data-id="element-118">
                      Objection Killers for the 7 most common pushbacks:
                    </span>
                    <ul className="mt-2 space-y-1 pl-4 border-l border-cyan-900/50 text-gray-400" data-id="element-119">
                      <li data-id="element-120">• "It's too expensive"</li>
                      <li data-id="element-121">• "Now isn't the right time"</li>
                      <li data-id="element-122">• "Can we do a trial?"</li>
                      <li data-id="element-123">• "I can do it in-house"</li>
                      <li data-id="element-124">• "I need to think about it"</li>
                      <li data-id="element-125">• "We tried content before"</li>
                      <li data-id="element-126">• "Why are you more expensive?"</li>
                    </ul>
                  </div>
                </li>
                {['High-Status Follow-Up System (24h / 72h / 7 days) — exact messages that maintain authority without chasing', '5 Plug-and-Play DM Sequences (4–6 messages each) for different lead types', 'Messaging Rules — the do/don\'t list that separates closers from people who "stay in conversation forever"'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-127">
                    <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-128" />
                    <span data-id="element-129">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-dark-950 p-4 rounded-lg border border-cyan-900/30" data-id="element-130">
              <strong className="text-cyan-400" data-id="element-131">The outcome:</strong> The next
              time you have a lead in your DMs, you know exactly what to say —
              from the first message to the close.
            </div>
          </div>
        </div>

        {/* 8. BONUS #3 CARD */}
        <div className="bg-dark-900 border border-cyan-500/20 rounded-xl p-8 mb-16 shadow-[0_0_30px_rgba(0,240,255,0.05)] relative overflow-hidden" data-id="element-132">
          <div className="absolute top-0 right-0 bg-cyan-500 text-black font-bold px-4 py-1 rounded-bl-lg text-sm" data-id="element-133">
            FREE - Value $97
          </div>

          <div className="flex items-center gap-3 mb-4" data-id="element-134">
            <CalendarIcon className="w-8 h-8 text-cyan-400" data-id="element-135" />
            <h3 className="text-3xl font-bold text-white" data-id="element-136">BONUS #3</h3>
          </div>
          <h4 className="text-2xl font-bold text-cyan-400 mb-2" data-id="element-137">
            The 4-Hours/Week Execution Planner
          </h4>
          <p className="text-xl text-gray-300 mb-8" data-id="element-138">
            "Burnout-Proof Build Schedule"
          </p>

          <div className="relative rounded-xl overflow-hidden border border-cyan-500/30 bg-dark-950 p-2 shadow-2xl mb-8 box-glow" data-id="element-139">
            <img src="https://cdn.magicpatterns.com/uploads/fdAZ6n7hxYLKvHrgeuyDx9/BONO_3_THE_10K_AI.png" alt="The 4-Hours/Week Execution Planner" className="w-full h-auto rounded-lg" data-id="element-140" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-900/60 via-transparent to-transparent pointer-events-none" data-id="element-141"></div>
          </div>

          <div className="space-y-6 text-gray-300" data-id="element-142">
            <div data-id="element-143">
              <strong className="text-white block mb-2" data-id="element-144">
                The problem it solves:
              </strong>
              <p data-id="element-145">
                "I don't have time." It's the most honest objection that exists.
                It's also the most solvable — with the right system.
              </p>
              <p className="mt-2" data-id="element-146">
                This planner is built specifically for the corporate
                professional who arrives home exhausted, has 4 hours per week
                maximum, and needs to know exactly what to do and when — without
                extra thinking, extra planning, or extra energy.
              </p>
            </div>

            <div data-id="element-147">
              <strong className="text-white block mb-4" data-id="element-148">What's inside:</strong>
              <ul className="space-y-3" data-id="element-149">
                <li className="flex items-start gap-3" data-id="element-150">
                  <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-151" />
                  <span data-id="element-152">
                    The Burnout-Proof Rule — why you should never mix "thinking"
                    and "executing" in the same session
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-153">
                  <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-154" />
                  <span data-id="element-155">
                    The 4-Hour Weekly Cadence — 5 blocks with exact durations
                    (60 / 90 / 45 / 30 / 15 min)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-156">
                  <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-157" />
                  <div data-id="element-158">
                    <span data-id="element-159">
                      3 Schedule Options — choose the one that fits your real
                      life:
                    </span>
                    <ul className="mt-2 space-y-1 pl-4 border-l border-cyan-900/50 text-gray-400" data-id="element-160">
                      <li data-id="element-161">• Two-Day Builder (Tue + Thu)</li>
                      <li data-id="element-162">• Weekend Warrior (Sat + Sun)</li>
                      <li data-id="element-163">• Micro Blocks (20–30 min daily)</li>
                    </ul>
                  </div>
                </li>
                {['The Energy Router — what to do with 2 minutes, 25 minutes, or 60 minutes based on your energy level', 'The No-Zero Days Checklist (5 min/day) — for the days you have zero motivation', 'The Weekly Task Map — Mon / Wed / Fri / Sun with exact tasks per day', 'The 21-Day Micro Sprint — Week 1: install cadence / Week 2: stabilize production / Week 3: improve conversion', 'The Anti-Guilt Tracker with 3 unique metrics (hours invested, outputs shipped, proof collected)', 'Tracker Table Template (Google Sheet ready to copy)'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-164">
                    <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-165" />
                    <span data-id="element-166">{item}</span>
                  </li>)}
                <li className="flex items-start gap-3" data-id="element-167">
                  <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-168" />
                  <div data-id="element-169">
                    <span data-id="element-170">3 Printable Checklists:</span>
                    <ul className="mt-2 space-y-1 pl-4 border-l border-cyan-900/50 text-gray-400" data-id="element-171">
                      <li data-id="element-172">
                        • "Before You Publish" — so you stop over-editing and
                        start shipping
                      </li>
                      <li data-id="element-173">
                        • "Before You DM" — to stay premium in every message
                      </li>
                      <li data-id="element-174">• "Before You Sleep" — so tomorrow starts easier</li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-dark-950 p-4 rounded-lg border border-cyan-900/30" data-id="element-175">
              <strong className="text-cyan-400" data-id="element-176">The outcome:</strong> This week
              you know exactly what to do, when to do it, and what to measure.
              No extra planning. No overwhelm. No guilt.
            </div>
          </div>
        </div>

        {/* 9. VALUE STACK TABLE */}
        <div className="bg-dark-900 border border-cyan-500/30 rounded-xl overflow-hidden mb-16 shadow-[0_0_40px_rgba(0,240,255,0.1)]" data-id="element-177">
          <div className="bg-cyan-950/50 p-4 border-b border-cyan-500/30" data-id="element-178">
            <h3 className="text-xl font-bold text-white text-center tracking-wide" data-id="element-179">
              HERE'S WHAT YOU'RE GETTING TODAY
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-180">
            <div className="flex justify-between items-center border-b border-gray-800 pb-4" data-id="element-181">
              <span className="text-gray-300 font-medium" data-id="element-182">
                The $10K AI Content Engine
              </span>
              <span className="text-white font-bold" data-id="element-183">$197</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-800 pb-4" data-id="element-184">
              <span className="text-gray-300" data-id="element-185">
                Bonus #1: 30-Minute Offer Clarity Kit
                <span className="ml-2 inline-block text-[10px] font-extrabold uppercase tracking-widest bg-cyan-400 text-black px-1.5 py-0.5 rounded align-middle" data-id="element-186">
                  FREE
                </span>
              </span>
              <span className="text-gray-500 font-bold line-through" data-id="element-187">$197</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-800 pb-4" data-id="element-188">
              <span className="text-gray-300" data-id="element-189">
                Bonus #2: Corporate-to-Creator Scripts
                <span className="ml-2 inline-block text-[10px] font-extrabold uppercase tracking-widest bg-cyan-400 text-black px-1.5 py-0.5 rounded align-middle" data-id="element-190">
                  FREE
                </span>
              </span>
              <span className="text-gray-500 font-bold line-through" data-id="element-191">$297</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-800 pb-4" data-id="element-192">
              <span className="text-gray-300" data-id="element-193">
                Bonus #3: 4-Hours/Week Execution Planner
                <span className="ml-2 inline-block text-[10px] font-extrabold uppercase tracking-widest bg-cyan-400 text-black px-1.5 py-0.5 rounded align-middle" data-id="element-194">
                  FREE
                </span>
              </span>
              <span className="text-gray-500 font-bold line-through" data-id="element-195">$97</span>
            </div>
            <div className="flex justify-between items-center pt-2" data-id="element-196">
              <span className="text-gray-400 font-bold" data-id="element-197">TOTAL REAL VALUE</span>
              <span className="text-gray-400 font-bold line-through" data-id="element-198">$788</span>
            </div>
            <div className="flex justify-between items-center pt-4 mt-4 border-t-2 border-cyan-500/50" data-id="element-199">
              <span className="text-2xl font-bold text-white" data-id="element-200">
                Your investment today
              </span>
              <span className="text-4xl font-bold text-cyan-400 text-glow" data-id="element-201">
                ${totalPrice.toFixed(2)}
              </span>
            </div>
            <div className="mt-4 text-center space-y-1 text-gray-400" data-id="element-202">
              <p data-id="element-203">That's less than a Starbucks order.</p>
              <p data-id="element-204">One client pays $2,500/month.</p>
              <p className="text-white font-medium" data-id="element-205">
                This is the map to get there.
              </p>
            </div>
          </div>
        </div>

        {/* 10. BIG PROMISE */}
        <div className="text-center mb-16" data-id="element-206">
          <h3 className="text-2xl font-bold text-white mb-6" data-id="element-207">
            In less than 48 hours you could have:
          </h3>
          <ul className="inline-block text-left space-y-4 text-lg text-gray-300" data-id="element-208">
            <li className="flex items-center gap-3" data-id="element-209">
              <CheckIcon className="w-6 h-6 text-cyan-400" data-id="element-210" />
              <span data-id="element-211">A $10K retainer offer defined and ready to send</span>
            </li>
            <li className="flex items-center gap-3" data-id="element-212">
              <CheckIcon className="w-6 h-6 text-cyan-400" data-id="element-213" />
              <span data-id="element-214">
                Exact scripts to open conversations and close without pressure
              </span>
            </li>
            <li className="flex items-center gap-3" data-id="element-215">
              <CheckIcon className="w-6 h-6 text-cyan-400" data-id="element-216" />
              <span data-id="element-217">
                A weekly plan that works even when you're completely drained
              </span>
            </li>
          </ul>
        </div>

        {/* 11. PROOF LAYER */}
        <div className="prose prose-invert prose-cyan max-w-none text-lg text-gray-300 space-y-6 mb-16 text-center" data-id="element-218">
          <p data-id="element-219">This isn't theory built in a vacuum.</p>
          <p data-id="element-220">These 3 resources were built from one question:</p>
          <p className="text-xl text-white font-bold italic" data-id="element-221">
            "What does a burned-out corporate professional actually need to move
            this week?"
          </p>
          <p data-id="element-222">
            Not more content. Not another framework. Not a motivation boost.
          </p>
          <p data-id="element-223">They need the offer written. The words ready. The time blocked.</p>
          <p className="text-cyan-400 font-bold" data-id="element-224">
            That's exactly what these 3 resources deliver.
          </p>
        </div>

        {/* 12. RISK REVERSAL */}
        <div className="bg-dark-900 border border-green-500/20 rounded-xl p-8 mb-16" data-id="element-225">
          <div className="flex flex-col items-center text-center gap-4" data-id="element-226">
            <ShieldCheckIcon className="w-10 h-10 text-green-400" data-id="element-227" />
            <h3 className="text-2xl font-bold text-white uppercase tracking-wide" data-id="element-228">
              30-DAY FULL GUARANTEE
            </h3>
            <p className="text-lg text-gray-300 leading-relaxed max-w-xl" data-id="element-229">
              If in the next 30 days you don't have your offer defined, your
              first scripts ready, and your system running — write to me. I'll
              refund every cent. No forms. No awkward questions.
            </p>
            <p className="text-cyan-400 font-medium" data-id="element-230">
              Why can I offer this? Because the people who implement this don't
              ask for refunds.
            </p>
          </div>
        </div>

        {/* 13. URGENCY / SCARCITY */}
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-16 text-center" data-id="element-231">
          <p className="text-sm font-bold text-amber-400 uppercase tracking-widest mb-2" data-id="element-232">
            LAUNCH PRICE
          </p>
          <p className="text-lg text-gray-300" data-id="element-233">
            The current price of $27 is a launch price. It increases soon. Once
            it goes up, it doesn't come back down.
          </p>
        </div>

        {/* 14. FAQ SECTION */}
        <div className="mb-16" data-id="element-234">
          <h3 className="text-3xl font-bold text-white mb-8 text-center" data-id="element-235">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4" data-id="element-236">
            {faqs.map((faq, index) => <div key={index} className="bg-dark-900 border border-cyan-900/30 rounded-lg overflow-hidden transition-all duration-200" data-id="element-237">
                <button onClick={() => toggleFaq(index)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-238">
                  <span className="text-lg font-bold text-white" data-id="element-239">{faq.q}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-cyan-400 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-240" />
                </button>
                {openFaq === index && <div className="px-6 pb-6 text-gray-300 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-241">
                    <div className="h-px w-full bg-cyan-900/30 mb-4" data-id="element-242"></div>
                    <p data-id="element-243">{faq.a}</p>
                  </div>}
              </div>)}
          </div>
        </div>

        {/* 15. PURCHASE CTA — DYNAMIC PRICE */}
        <div className="text-center" data-id="element-244">
          <div className="text-4xl font-bold text-white mb-3 flex items-center justify-center gap-3" data-id="element-245">
            <span className="text-xl text-gray-500 font-normal line-through" data-id="element-246">
              ${originalPrice}
            </span>
            <span className="text-cyan-400 text-glow" data-id="element-247">
              ${totalPrice.toFixed(2)}
            </span>
            <span className="text-base text-gray-500 font-normal" data-id="element-248">
              One-Time
            </span>
          </div>
          <a href={cartUrl} target="_blank" rel="noopener noreferrer" onClick={onPurchaseClick} className="w-full max-w-lg mx-auto group relative inline-flex items-center justify-center px-8 py-5 font-bold text-black bg-gradient-to-r from-cyan-400 to-cyan-300 rounded-sm overflow-hidden transition-all hover:from-cyan-300 hover:to-cyan-200 hover:shadow-[0_0_40px_rgba(0,240,255,0.6)] mb-3" data-id="element-249">
            <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-white rounded-full group-hover:w-full group-hover:h-56 opacity-10" data-id="element-250"></span>
            <span className="relative text-lg tracking-wide uppercase flex items-center gap-2" data-id="element-251">
              <ZapIcon className="w-5 h-5 fill-current" data-id="element-252" /> Get Instant Access —
              ${totalPrice.toFixed(2)}
            </span>
          </a>
          <div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-400 mt-2" data-id="element-253">
            <div className="flex items-center gap-2" data-id="element-254">
              <ShieldCheckIcon className="w-4 h-4 text-green-400" data-id="element-255" />
              <span data-id="element-256">Secure Checkout • Instant Delivery</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-257">
              <ArrowRightIcon className="w-4 h-4 text-cyan-400" data-id="element-258" />
              <span data-id="element-259">30-Day Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}